import React, { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  addEntry,
  DefectReason,
  errorMessage,
  fetchDefectReasons,
  fetchInstruction,
  fetchInstructionFile,
  PackingInstruction,
  PackingRow,
  setFlags,
  setPriority,
} from './packingApi';
import { ACCENT, btn, dialog, input, overlay } from './packingStyles';

/** pdf.js is large — loaded only when an instruction is opened. */
const PdfViewer = lazy(() => import('../common/PdfViewer'));

export type PackingAction = 'pack' | 'defect' | 'labels' | 'priority';

interface Props {
  action: PackingAction;
  row: PackingRow;
  onClose: () => void;
  onDone: () => void;
}

/**
 * Pack / defect / missing labels / priority note for one row — one small touch-friendly dialog.
 * A defect is picked from the manager's list (no typing); packing offers the packing instruction.
 */
const PackingActionModal: React.FC<Props> = ({ action, row, onClose, onDone }) => {
  const { t } = useLanguage();
  const name = row.nazwa || row.indeks || '';
  const [quantity, setQuantity] = useState(
    action === 'pack' ? String(row.to_pack) : action === 'labels' ? String(row.labels_missing_qty ?? '') : '',
  );
  const [note, setNote] = useState(action === 'priority' ? row.priority_note ?? '' : '');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [reasons, setReasons] = useState<DefectReason[] | null>(null);
  const [reasonId, setReasonId] = useState<number | null>(null);
  const [instruction, setInstruction] = useState<PackingInstruction | null | undefined>(undefined);
  const [showInstruction, setShowInstruction] = useState(false);
  const instructionId = instruction?.id;
  // Stable callbacks: the viewer reloads the PDF only when the instruction changes.
  const loadInstruction = useCallback(() => fetchInstructionFile(instructionId as number), [instructionId]);
  const closeInstruction = useCallback(() => setShowInstruction(false), []);

  useEffect(() => {
    if (action === 'defect') {
      fetchDefectReasons()
        .then((list) => setReasons(list.filter((r) => r.is_active)))
        .catch((e) => setError(errorMessage(e, t('packing.errorLoad'))));
    } else if (action === 'pack') {
      fetchInstruction(row.pivot_id).then(setInstruction).catch(() => setInstruction(null));
    }
  }, [action, row.pivot_id, t]);

  const needsQuantity = action !== 'priority';
  const capped = action === 'pack' || action === 'defect';
  const qty = parseInt(quantity, 10);
  const tooMany = capped && Number.isFinite(qty) && qty > row.to_pack;
  const title = t(`packing.modal.${action}Title`, { name });

  const run = async (call: () => Promise<unknown>) => {
    setSaving(true);
    setError(null);
    try {
      await call();
      onDone();
    } catch (e: any) {
      setError(errorMessage(e, t('packing.errorSave')));
    } finally {
      setSaving(false);
    }
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (action === 'pack' || action === 'defect') {
      void run(() => addEntry(row.pivot_id, action === 'pack' ? 'packed' : 'defect', qty, reasonId));
    } else if (action === 'labels') {
      void run(() => setFlags(row.pivot_id, { labels_missing_qty: qty }));
    } else {
      void run(() => setPriority(row.pivot_id, note.trim() || null));
    }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <form style={dialog(showInstruction ? 640 : 460)} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h2 style={{ margin: '0 0 6px', fontSize: 19, color: '#0f172a' }}>{title}</h2>
        <div style={{ fontSize: 13, color: '#64748b', marginBottom: action === 'pack' ? 10 : 16 }}>
          {[row.indeks, row.linia, row.tydzien && `${t('packing.col.week')} ${row.tydzien}`].filter(Boolean).join(' · ')}
        </div>
        {action === 'pack' && (row.szpula || row.marka) && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            {row.szpula && (
              <span style={{ padding: '6px 12px', borderRadius: 8, background: '#eef2ff', color: '#3730a3', fontSize: 18, fontWeight: 800 }}>
                {t('packing.col.spool')}: {row.szpula}
              </span>
            )}
            {row.marka && (
              <span style={{ padding: '6px 12px', borderRadius: 8, background: '#f1f5f9', color: '#0f172a', fontSize: 18, fontWeight: 800 }}>
                {t('packing.col.brand')}: {row.marka}
              </span>
            )}
          </div>
        )}

        {action === 'pack' && (
          <div style={{ marginBottom: 14 }}>
            <button
              type="button"
              style={{ ...btn('ghost'), width: '100%', borderColor: instruction ? ACCENT : '#cbd5e1', color: instruction ? ACCENT : '#94a3b8' }}
              disabled={!instruction}
              onClick={() => setShowInstruction(true)}
            >
              {instruction === undefined
                ? t('packing.instruction.loading')
                : instruction === null
                  ? t('packing.instruction.none')
                  : t('packing.instruction.show')}
            </button>
            {showInstruction && instruction && (
              <Suspense fallback={null}>
                <PdfViewer
                  title={
                    instruction.scope === 'index'
                      ? t('packing.instruction.forIndex', { index: instruction.catalog_index ?? '' })
                      : t('packing.instruction.forBrand', { brand: instruction.brand ?? '' })
                  }
                  load={loadInstruction}
                  onClose={closeInstruction}
                />
              </Suspense>
            )}
          </div>
        )}

        {needsQuantity && (
          <label style={{ display: 'block', marginBottom: 12, fontSize: 14, fontWeight: 600, color: '#334155' }}>
            {action === 'labels' ? t('packing.modal.labelsQty') : t('packing.modal.quantity')}
            <input
              autoFocus
              type="number"
              inputMode="numeric"
              min={1}
              step={1}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              style={{ ...input, marginTop: 6, fontSize: 22, fontWeight: 700, borderColor: tooMany ? '#dc2626' : '#cbd5e1' }}
            />
            {capped && (
              <span style={{ display: 'block', marginTop: 6, fontSize: 13, color: tooMany ? '#dc2626' : '#64748b' }}>
                {tooMany ? t('packing.modal.tooMany', { n: row.to_pack }) : t('packing.modal.max', { n: row.to_pack })}
              </span>
            )}
          </label>
        )}

        {action === 'defect' && (
          <fieldset style={{ border: 'none', padding: 0, margin: '0 0 12px' }}>
            <legend style={{ fontSize: 14, fontWeight: 600, color: '#334155', marginBottom: 8, padding: 0 }}>{t('packing.modal.reason')}</legend>
            {reasons === null ? (
              <div style={{ color: '#94a3b8', fontSize: 14 }}>{t('packing.instruction.loading')}</div>
            ) : reasons.length === 0 ? (
              <div style={{ color: '#b91c1c', fontSize: 14 }}>{t('packing.modal.noReasons')}</div>
            ) : (
              <div role="radiogroup" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
                {reasons.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    role="radio"
                    aria-checked={reasonId === r.id}
                    onClick={() => setReasonId(r.id)}
                    style={{
                      ...btn(reasonId === r.id ? 'danger' : 'ghost'),
                      whiteSpace: 'normal',
                      textAlign: 'left',
                      lineHeight: 1.25,
                    }}
                  >
                    {r.name}
                  </button>
                ))}
              </div>
            )}
          </fieldset>
        )}

        {action === 'priority' && (
          <label style={{ display: 'block', marginBottom: 12, fontSize: 14, fontWeight: 600, color: '#334155' }}>
            {t('packing.modal.priorityNote')}
            <input
              autoFocus
              type="text"
              maxLength={255}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('packing.modal.priorityPlaceholder')}
              style={{ ...input, marginTop: 6 }}
            />
          </label>
        )}

        {error && (
          <div style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 12px', fontSize: 14, marginBottom: 12 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: 8 }}>
          {action === 'priority' && row.priority_note && (
            <button type="button" style={btn('ghost')} disabled={saving} onClick={() => void run(() => setPriority(row.pivot_id, null))}>
              {t('packing.modal.clearPriority')}
            </button>
          )}
          <button type="button" style={btn('ghost')} onClick={onClose}>
            {t('packing.modal.cancel')}
          </button>
          <button
            type="submit"
            style={{ ...btn(action === 'defect' ? 'danger' : 'primary'), opacity: saving || tooMany || (action === 'defect' && reasonId === null) ? 0.6 : 1 }}
            disabled={saving || tooMany || (needsQuantity && !(qty > 0)) || (action === 'defect' && reasonId === null)}
          >
            {t('packing.modal.save')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PackingActionModal;
